import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiPostRetry, friendlyAuthError } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in

  const checkSession = useCallback(async () => {
    const token = localStorage.getItem("pac_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      localStorage.removeItem("pac_token");
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    try {
      const { data } = await apiPostRetry("/auth/login", { email, password });
      if (data.twofa_required) {
        return { ok: true, twofa: true, challengeId: data.challenge_id, emailMasked: data.email_masked, devCode: data.dev_code };
      }
      localStorage.setItem("pac_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: friendlyAuthError(e) };
    }
  };

  const verifyOtp = async (challengeId, code) => {
    try {
      const { data } = await apiPostRetry("/auth/verify-otp", { challenge_id: challengeId, code });
      localStorage.setItem("pac_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: friendlyAuthError(e) };
    }
  };

  const resendOtp = async (challengeId) => {
    try {
      const { data } = await apiPostRetry("/auth/resend-otp", { challenge_id: challengeId });
      return { ok: true, challengeId: data.challenge_id, emailMasked: data.email_masked, devCode: data.dev_code };
    } catch (e) {
      return { ok: false, error: friendlyAuthError(e) };
    }
  };

  const logout = () => {
    localStorage.removeItem("pac_token");
    setUser(false);
  };

  return <AuthContext.Provider value={{ user, login, verifyOtp, resendOtp, logout }}>{children}</AuthContext.Provider>;
};
